import { Tour, TourProps, TourStepProps } from 'antd';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setTour, unsetTour } from '@/store/resellerTour/slice';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerTourState } from '@/store/resellerTour/selector';
import { AppRoutes } from 'src/enums/ERoutes';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTourStatus } from '@/api/reseller';
import { getResellerData } from '@/store/reseller/selector';

const ResellerTour: React.FC<{
	tourSteps?: TourStepProps[];
}> = ({ tourSteps }) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const resellerTour = useAppSelector(getResellerTourState);
	const resellerData = useAppSelector(getResellerData);

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleClose = () => {
		const returnTo = resellerTour.returnTo;
		dispatch(unsetTour());

		if (returnTo) {
			navigate(returnTo);
		}
	};

	const handleNextPrevStep = ({ step, route }: { step: number; route?: AppRoutes }) => {
		const pageOnlyTour = resellerTour.returnTo !== AppRoutes.RESELLER_ONBOARDING;

		// if we have the Onboarding tour opened, we will checkmark the completed steps so that
		// the reseller won't have to go through them again when they visit the specific page
		if (!pageOnlyTour && step > 4) {
			if (step === 5 && !resellerData?.tours?.receptionist) {
				updateToursMutation.mutate('receptionist');
			} else if (step === 6 && !resellerData?.tours?.branding) {
				updateToursMutation.mutate('branding');
			} else if (step === 7 && !resellerData?.tours?.academy) {
				updateToursMutation.mutate('academy');
			} else if (step >= 8 && !resellerData?.tours?.contact_support) {
				updateToursMutation.mutate('contact_support');
			}
		}

		dispatch(
			setTour({
				...resellerTour,
				currentStep: step,
				open: true,
			}),
		);

		if (!pageOnlyTour && route) {
			navigate(route);
		}
	};

	const onboardingSteps: TourStepProps[] = [
		{
			title: <h1 className="font-bold text-2xl">Welcome to the White Label Program!</h1>,
			description: (
				<p className="text-base">
					Purchase receptionists at a discounted rate and set your own margins! Onboard unlimited
					clients and scale business without limits.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 1 }),
		},
		{
			title: <h1 className="font-bold text-2xl">Scaling has never been easier!</h1>,
			description: (
				<p className="text-base">
					Right now, less then 6% of small businesses in the U.S. are using AI in their business.
					It's the perfect time to get in early and build something big.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 2 }),
			onPrev: () => handleNextPrevStep({ step: 0 }),
		},
		{
			title: <h1 className="font-bold text-2xl">If you win, we win!</h1>,
			description: (
				<p className="text-base">
					Here's the bottom line: we handle the tech so you can focus on your business. We're the
					only AI company built specifically for small businesses - and we'll be right here as you
					scale.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 3 }),
			onPrev: () => handleNextPrevStep({ step: 1 }),
		},
		{
			title: <h1 className="font-bold text-2xl">Proper Support is everything</h1>,
			description: (
				<p className="text-base">
					If you're in, we're all in with you. Chat support 24/7, business and product calls, we are
					here to make it easy for you to scale.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 4 }),
			onPrev: () => handleNextPrevStep({ step: 2 }),
		},
		{
			title: <h1 className="font-bold text-2xl">Explore Your Admin Dashboard</h1>,
			description: (
				<p className="text-base">
					Everything you need to run your AI reselling business—client management, billing,
					branding, and more—is all in one place. Let's take a quick tour.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 5, route: AppRoutes.RESELLER_PORTAL }),
			onPrev: () => handleNextPrevStep({ step: 3 }),
		},
		{
			title: <h1 className="font-bold text-2xl">All Your Client Accounts in One Place</h1>,
			description: (
				<p className="text-base">
					Manage all your receptionists from one place. You'll also find embeddable login and
					registration links to add to your website, giving your clients direct access under your
					brand.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 6, route: AppRoutes.RESELLER_BRANDING }),
			onPrev: () => handleNextPrevStep({ step: 4, route: resellerTour.returnTo as AppRoutes }),
			placement: 'topLeft',
		},
		{
			title: <h1 className="font-bold text-2xl">Your branding everywhere!</h1>,
			description: (
				<p className="text-base">
					Add your logo, company name, and customize the dashboard to match your brand. You can also
					add two custom tabs to your clients' dashboard and choose which features they can access.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 7, route: AppRoutes.RESELLER_ACADEMY }),
			onPrev: () => handleNextPrevStep({ step: 5, route: AppRoutes.RESELLER_PORTAL }),
			placement: 'topLeft',
		},
		{
			title: <h1 className="font-bold text-2xl">Reseller Academy - Your Own Knowledge Base</h1>,
			description: (
				<p className="text-base">
					Access reseller and product step-by-step tutorials and videos here and learn how to set
					everything up properly. Replay this tutorial from here as well.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 8, route: AppRoutes.RESELLER_CONTACT_SUPPORT }),
			onPrev: () => handleNextPrevStep({ step: 6, route: AppRoutes.RESELLER_BRANDING }),
			placement: 'topLeft',
		},
		{
			title: <h1 className="font-bold text-2xl">Need help setting up?</h1>,
			description: (
				<p className="text-base">
					Need a hand getting started? **Book a free onboarding call** and we'll walk you through
					everything, step by step.
				</p>
			),
			onNext: () => handleNextPrevStep({ step: 9 }),
			onPrev: () => handleNextPrevStep({ step: 7 }),
			placement: 'topLeft',
		},
		{
			title: <h1 className="font-bold text-2xl">Tutorial Complete</h1>,
			description: (
				<p className="text-base">
					Need help? Chat with us 24/7 via the Contact Support tab. Explore the dashboard, register
					your first client, and replay this tutorial anytime in Tutorials. Let's start scaling
					together!
				</p>
			),
			onPrev: () => handleNextPrevStep({ step: 8, route: AppRoutes.RESELLER_CONTACT_SUPPORT }),
			placement: 'topLeft',
		},
	];

	const buttonStyling = {
		nextButtonProps: {
			className: '!bg-blue-600 hover:!bg-blue-600 !px-4 !h-8',
			children:
				resellerTour.currentStep ===
				(
					(resellerTour.returnTo === AppRoutes.RESELLER_ONBOARDING
						? onboardingSteps
						: tourSteps) as TourStepProps[]
				)?.length -
					1 ? (
					<div className="flex items-center justify-center gap-2">
						<span>Finish</span>
						<CheckOutlined className="w-3 h-3" />
					</div>
				) : (
					<div className="flex items-center justify-center gap-2">
						<span>Next</span>
						<ArrowRightOutlined className="w-3 h-3" />
					</div>
				),
		},
		prevButtonProps: {
			className: '!px-4 !h-8 !outline-none !border-none !shadow-none !hover:bg-transparent',
			children: (
				<div className="flex items-center justify-center gap-2">
					<ArrowLeftOutlined className="w-3 h-3" />
					<span>Previous</span>
				</div>
			),
		},
	};

	const getSteps = (): TourProps['steps'] => {
		switch (resellerTour.returnTo) {
			case AppRoutes.RESELLER_ONBOARDING:
				return onboardingSteps.map((step) => ({ ...step, ...buttonStyling }));
			default:
				return tourSteps
					? tourSteps?.map((step, index) => ({
							...step,
							...buttonStyling,
							...(index !== 0 && { onPrev: () => handleNextPrevStep({ step: index - 1 }) }),
							...(index !== tourSteps?.length - 1 && {
								onNext: () => handleNextPrevStep({ step: index + 1 }),
							}),
						}))
					: onboardingSteps.map((step) => ({ ...step, ...buttonStyling }));
		}
	};

	const steps = getSteps();

	return (
		<Tour
			open={resellerTour.open}
			steps={steps}
			current={resellerTour.currentStep}
			onClose={handleClose}
		/>
	);
};

export default ResellerTour;
